(ns minorodata.components.analytics-models.steps
  "Step bodies for the analytics-model create wizard / edit tabs.

   All steps are pure *views* over a single shared form-model's `fields` map
   (the `{field-name {:value .. :action ..}}` shape produced by
   `mold-forms.core`). The form-model is owned by the wizard root and never
   unmounts, so steps can mount/unmount freely without losing state.

   The Details step reuses the connection form-generator's block renderer; the
   Inputs/Outputs and Schedule steps are bespoke (not generator blocks)."
  (:require [clojure.string :as str]
            [minorodata.analytics-models.domain :as domain]
            [minorodata.app.utils :as app.utils]
            [minorodata.components.analytics-models.docs :as docs]
            [minorodata.components.analytics-models.schema :as schema]
            [minorodata.components.connections.forms.generator :as gen]
            [minorodata.components.schedule.core :as c.schedule]
            [ui.alert :as ui.alert]
            [ui.typography :as typography]))


;; -----------------------------------------------------------------------------
;; Field & block definitions for the single shared form-model
;; -----------------------------------------------------------------------------

(def prelude-blocks
  "The name + description generator blocks every model's Details step starts with."
  [{:block-type :input
    :label      "Name"
    :required   true
    :name       :name
    :value      ""}
   {:block-type :input
    :multiline  true
    :label      "Description"
    :name       :description
    :value      ""}])


(defn table-field-name
  "Form-field key for a table's editable name (e.g. :input-table:foo)."
  [direction table-name]
  (keyword (str (name direction) "-table:" table-name)))


(defn- table-defs [model]
  (:analytics-model-registry/table-definitions model))


(defn detail-blocks
  "Generator blocks rendered on the Details step: name, description and the
   model's own parameter blocks (`custom-ui-blocks`)."
  [model]
  (let [params (seq (:analytics-model-registry/custom-ui-blocks model))]
    (concat prelude-blocks
            (when params
              (concat [{:block-type :subtitle :content "Parameters"}] params)))))


(defn- table-name-fields
  "One form-field per input/output table, holding the (editable) table name.
   Keyed via `table-field-name` so the I/O graph nodes bind to them."
  [model]
  (let [{:keys [input-tables output-tables]} (table-defs model)
        ->field (fn [direction {:keys [table-name]}]
                  {:name    (table-field-name direction table-name)
                   :value   table-name
                   :process identity})]
    (concat (map (partial ->field :input)  input-tables)
            (map (partial ->field :output) output-tables))))


(defn- blank-value? [v]
  (or (nil? v) (and (string? v) (str/blank? v))))


(defn details-valid?
  "True when every required Details field has a value — the name plus any
   `custom-ui-blocks` param flagged `:required`. Gates the wizard's Next button."
  [model fields]
  (->> (detail-blocks model)
       (filter :required)
       (every? (fn [{:keys [name]}]
                 (not (blank-value? (get-in fields [name :value])))))))


(defn unset-required-tables
  "Placeholders of required tables missing a real value (blank or still the
   placeholder). Inputs are optional when flagged `:can-be-empty`; outputs are
   always required. `value-of` reads a field-name's value."
  [model value-of]
  (let [{:keys [input-tables output-tables]} (table-defs model)
        unset (fn [direction tables]
                (->> tables
                     (keep (fn [{:keys [table-name]}]
                             (let [v (value-of (table-field-name direction table-name))]
                               (when (or (blank-value? v) (= v table-name))
                                 table-name))))))]
    (concat (unset :input  (remove :can-be-empty input-tables))
            (unset :output output-tables))))


(defn unset-required-tables-from-fields
  "`unset-required-tables` reading from the `fields` map used in step bodies."
  [model fields]
  (unset-required-tables model #(get-in fields [% :value])))


(defn unset-required-tables-from-data
  "`unset-required-tables` reading from the flattened `model->fields` submit data."
  [model data]
  (unset-required-tables model #(get data %)))


(defn required-tables-error
  "Human message naming the required tables still missing a value, or nil when
   none are missing."
  [missing]
  (when (seq missing)
    (str "Set a name for the following required "
         (if (= 1 (count missing)) "table" "tables")
         ": " (str/join ", " missing) ".")))


(defn io-valid?
  "True when every required input/output table has a value. Gates the I/O
   step's Next (wizard) / Save (edit) button."
  [model fields]
  (empty? (unset-required-tables-from-fields model fields)))


(def schedule-required-fields
  "Cron fields that must be set for a non-empty schedule to be valid."
  [:minute])


(defn schedule-valid?
  "A schedule is valid when it's empty (`empty-cron` → not scheduled) or every
   required cron field is set. A partially-filled cron (e.g. an hour but no
   minute) is invalid."
  [fields]
  (let [cron (get-in fields [:cron :value])]
    (or (str/blank? cron)
        (= cron app.utils/empty-cron)
        (let [sched (app.utils/cron->schedule cron)]
          (not-any? #(contains? #{[] "*" nil} (get sched %)) schedule-required-fields)))))


(defn form-fields
  "Default field definitions for the shared form-model — covers every step.
   Callers merge edit prefill over these with `gen/merge-initial-values`."
  [model]
  (concat (gen/blocks->fields (detail-blocks model))
          (table-name-fields model)
          [{:name :cron :value app.utils/empty-cron :process identity}]))


(defn- param-initial-values
  "Prefill for the model's `custom-ui-blocks` params, whose block names are
   underscored but whose `model-params` keys read back kebab-cased."
  [model model-params]
  (into {} (keep (fn [{block-name :name}]
                   (when-let [k (domain/mapping-key block-name)]
                     (let [v (get model-params k)]
                       (when (some? v) [block-name v]))))
                 (:analytics-model-registry/custom-ui-blocks model))))


(defn edit-initial-values
  "Field-name → value map prefilling the form-model when editing an instance.
   Table names come from `model-params` `input_tables`/`output_tables` (read
   kebab-cased, keyed by placeholder), falling back to the positional vectors
   when that mapping is empty or absent."
  [model instance]
  (let [{:keys [input-tables output-tables]} (table-defs model)
        model-params (:analytics-model/model-params instance)
        table-init (fn [direction reg-tables inst-names mappings]
                     (into {} (map-indexed
                               (fn [i {:keys [table-name]}]
                                 [(table-field-name direction table-name)
                                  (if (seq mappings)
                                    (get mappings (domain/mapping-key table-name) "")
                                    (nth (vec inst-names) i table-name))])
                               reg-tables)))]
    (merge (param-initial-values model model-params)
           {:name        (:analytics-model/name instance)
            :description (:analytics-model/description instance)
            :cron        (or (:cron (:analytics-model/schedule-spec instance))
                             app.utils/empty-cron)}
           (table-init :input  input-tables  (:analytics-model/input-tables instance)  (:input-tables model-params))
           (table-init :output output-tables (:analytics-model/output-tables instance) (:output-tables model-params)))))


;; -----------------------------------------------------------------------------
;; Step bodies
;; -----------------------------------------------------------------------------

(defn details-step [{:keys [model form-id fields errors]}]
  (into [:div {:class "flex flex-col gap-4"}]
        (map #(gen/render-input-block % {:form-id form-id
                                         :fields  fields
                                         :errors  errors})
             (detail-blocks model))))


(defn io-step [{:keys [model form-id fields]}]
  ;; The graph renders editable table-name inputs (bound to the shared
  ;; form-model); the detail below lists each table's columns.
  (let [unset (unset-required-tables-from-fields model fields)]
    [:div {:class "flex flex-col gap-6"}
     [typography/p {:class "text-kleene-text-secondary"}
      "Edit the names in the graph below to set the full table names this model reads from and writes to."]
     (when-let [msg (required-tables-error unset)]
       [ui.alert/alert {:variant :error :title "Required tables not set"}
        [typography/p msg]])
     [schema/schema-panel {:model model :form-id form-id}]
     [docs/tables-detail model]]))


(defn schedule-step [{:keys [fields timezone]}]
  [:div {:class "flex flex-col gap-4"}
   [typography/h4 (str "Schedule" (when timezone (str " (" timezone " time zone)")))]
   [typography/p {:class "text-kleene-text-secondary"}
    (app.utils/format-schedule {:cron            (get-in fields [:cron :value])
                                :empty-status    "Not scheduled"
                                :required-fields schedule-required-fields})]
   [:hr {:class "border-kleene-layout-border"}]
   [c.schedule/cron-editor {:field (:cron fields)}]])


(defn- summary-row [label value]
  [:div {:class "flex flex-col gap-0.5"}
   [typography/label {:class "text-kleene-text-secondary"} label]
   [typography/p (if (str/blank? (str value)) "—" value)]])


(defn review-step [{:keys [model fields]}]
  (let [v        (fn [k] (get-in fields [k :value]))
        {:keys [input-tables output-tables]} (table-defs model)
        table-name (fn [direction {:keys [table-name]}]
                     (v (table-field-name direction table-name)))]
    [:div {:class "flex flex-col gap-4"}
     [summary-row "Name" (v :name)]
     [summary-row "Description" (v :description)]
     (when (seq input-tables)
       [summary-row "Input tables" (str/join ", " (map (partial table-name :input) input-tables))])
     (when (seq output-tables)
       [summary-row "Output tables" (str/join ", " (map (partial table-name :output) output-tables))])
     [summary-row "Schedule"
      (app.utils/format-schedule {:cron         (v :cron)
                                  :empty-status "Not scheduled"
                                  :required-fields schedule-required-fields})]]))


;; -----------------------------------------------------------------------------
;; Submit payload
;; -----------------------------------------------------------------------------

(defn ->submit-params
  "Build a create/update-model payload from the flattened form-model `data`.
   Table-name fields become the top-level `:input-tables`/`:output-tables`
   vectors; their placeholder→name mappings go in `model-params` under
   `input_tables`/`output_tables`. Folds cron into the schedule; `:status`
   defaults to `active`."
  [{:keys [data model project-name version status]}]
  (let [{:keys [input-tables output-tables]} (table-defs model)
        table-keys  (->> (concat (map #(table-field-name :input  (:table-name %)) input-tables)
                                 (map #(table-field-name :output (:table-name %)) output-tables))
                         set)
        ;; Resolved table name, or nil when unset (blank or still the
        ;; placeholder) — such tables are dropped rather than sent.
        table-value (fn [direction {:keys [table-name]}]
                      (let [v (get data (table-field-name direction table-name) table-name)]
                        (when-not (or (str/blank? v) (= v table-name))
                          v)))
        ->mappings  (fn [direction tables]
                      (into {} (keep (fn [t]
                                       (when-let [v (table-value direction t)]
                                         [(:table-name t) v]))
                                     tables)))
        input-mappings  (->mappings :input  input-tables)
        output-mappings (->mappings :output output-tables)
        cron        (:cron data)
        ;; Everything left in `data` is a model param; the reserved keys are
        ;; rebuilt below as fresh underscore-keyed mappings.
        params      (cond-> (apply dissoc data :name :description :cron
                                   :input-tables :output-tables table-keys)
                      (seq input-mappings)  (assoc :input_tables  input-mappings)
                      (seq output-mappings) (assoc :output_tables output-mappings))]
    (cond-> {:name          (:name data)
             :project-name  project-name
             :params        params
             :input-tables  (into [] (keep (partial table-value :input)  input-tables))
             :output-tables (into [] (keep (partial table-value :output) output-tables))
             :status        (or status "active")}
      version                          (assoc :version version)
      (not (str/blank? (:description data))) (assoc :description (:description data))
      (and cron (not= cron app.utils/empty-cron)) (assoc :cron cron))))
